// frontend/src/pages/public/ContactPage.jsx
import React, { useState } from "react";
import Seo from "../../marketing/Seo";
import { Container, Section, Eyebrow, Button } from "../../marketing/ui";

const SUPPORT_EMAIL = "support@qrflow.ioanna.ro";

/**
 * ContactPage — a validated enquiry form. Since there is no public contact API,
 * a valid submission opens the user's mail client via a prefilled mailto: link
 * (a reliable, backend-free submission path). The support address is also shown
 * directly as a fallback.
 */
export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email address.";
    if (form.message.trim().length < 10) next.message = "Please add a little more detail (10+ characters).";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const subject = encodeURIComponent(`QRFlow enquiry from ${form.name}`);
    const body = encodeURIComponent(`${form.message}\n\n— ${form.name} (${form.email})`);
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  const field = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500";

  return (
    <>
      <Seo
        title="Contact"
        description="Get in touch with the QRFlow team — sales, support, and partnership enquiries."
        path="/contact"
      />

      <Section>
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>Contact</Eyebrow>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Talk to us
            </h1>
            <p className="mt-5 text-lg leading-8 text-gray-600">
              Questions about plans, the API, or whether QRFlow fits your use-case? Send a
              message and we'll get back to you.
            </p>
            <p className="mt-6 text-sm text-gray-600">
              Prefer email? Reach us directly at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-brand-700 hover:text-brand-800">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Looking for help articles? Visit the{" "}
              <a href="/docs" className="font-medium text-brand-700 hover:text-brand-800">documentation</a>.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 p-6 sm:p-8">
            {sent ? (
              <div role="status" className="text-center">
                <h2 className="text-lg font-semibold text-gray-900">Thanks — your email is ready to send</h2>
                <p className="mt-2 text-sm text-gray-600">
                  We've opened your mail app with the message pre-filled. If nothing happened,
                  email us at{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-brand-700">{SUPPORT_EMAIL}</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="space-y-5">
                <div>
                  <label htmlFor="name" className="mb-1 block text-sm font-medium text-gray-700">Name</label>
                  <input id="name" type="text" value={form.name} onChange={update("name")} className={field} />
                  {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                  <input id="email" type="email" value={form.email} onChange={update("email")} className={field} />
                  {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="message" className="mb-1 block text-sm font-medium text-gray-700">Message</label>
                  <textarea id="message" rows={5} value={form.message} onChange={update("message")} className={field} />
                  {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
                </div>
                <Button type="submit" className="w-full">Send message</Button>
              </form>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}
